import { useEffect, useRef, useState } from 'react'
import { useStore } from '../store/AppStore'
import { openTasks, studyStreak, weekStudy } from '../store/selectors'
import { fmtClock, fmtMins, isOverdue, relDay, today, weekdayNames } from '../lib/date'
import { Button, Card, Check, Empty, Field, Meta, ScreenHead, Sheet, hueVars } from '../components/ui'
import { IconPlus, IconTrash } from '../components/icons'
import { HUES, type Hue, type Task } from '../store/types'
import { useT } from '../i18n'

type Phase = 'focus' | 'break'

export default function Study() {
  const { state, addTask, toggleTask, removeTask, logSession } = useStore()
  const [tab, setTab] = useState<'focus' | 'tasks' | 'subjects'>('focus')
  const t = useT()
  const streak = studyStreak(state)

  return (
    <>
      <ScreenHead
        title={t('study.title')}
        sub={streak > 1 ? t('study.streakKeep', { n: streak }) : t('study.oneSession')}
      />

      <div className="tabs" role="tablist" aria-label={t('study.title')}>
        {(
          [
            ['focus', t('study.tabFocus')],
            ['tasks', t('study.tabTasks')],
            ['subjects', t('study.tabSubjects')],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            role="tab"
            className="tabs__tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'focus' && <FocusTab onLog={logSession} />}
      {tab === 'tasks' && (
        <TasksTab
          tasks={state.tasks}
          onAdd={addTask}
          onToggle={toggleTask}
          onRemove={removeTask}
        />
      )}
      {tab === 'subjects' && <SubjectsTab />}
    </>
  )
}

/* ================================ focus ================================== */

function FocusTab({ onLog }: { onLog: ReturnType<typeof useStore>['logSession'] }) {
  const { state } = useStore()
  const t = useT()
  const dow = weekdayNames('narrow')
  const { focusMins, breakMins } = state.settings
  const [phase, setPhase] = useState<Phase>('focus')
  const [remaining, setRemaining] = useState(focusMins * 60)
  const [running, setRunning] = useState(false)
  const [subjectId, setSubjectId] = useState(state.subjects[0]?.id)
  const completedRef = useRef(false)

  const total = (phase === 'focus' ? focusMins : breakMins) * 60

  /* Reset the clock when the configured lengths change while idle. */
  useEffect(() => {
    if (!running) setRemaining((phase === 'focus' ? focusMins : breakMins) * 60)
  }, [focusMins, breakMins, phase, running])

  useEffect(() => {
    if (!running) return
    const id = window.setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000)
    return () => window.clearInterval(id)
  }, [running])

  /* Phase completion: log the focus block, then flip to the other phase. */
  useEffect(() => {
    if (remaining > 0 || !running || completedRef.current) return
    completedRef.current = true
    setRunning(false)
    if (phase === 'focus') {
      onLog({ subjectId, mins: focusMins })
      setPhase('break')
      setRemaining(breakMins * 60)
    } else {
      setPhase('focus')
      setRemaining(focusMins * 60)
    }
    // Let the next tick re-arm completion.
    window.setTimeout(() => (completedRef.current = false), 50)
  }, [remaining, running, phase, focusMins, breakMins, subjectId, onLog])

  const pct = total ? ((total - remaining) / total) * 100 : 0
  const subject = state.subjects.find((s) => s.id === subjectId)
  const hue: Hue = phase === 'break' ? 'teal' : subject?.hue ?? 'iris'
  const week = weekStudy(state)

  const reset = () => {
    setRunning(false)
    setRemaining((phase === 'focus' ? focusMins : breakMins) * 60)
  }

  /** Bank the elapsed part of an interrupted focus block rather than lose it. */
  const stopAndLog = () => {
    const done = Math.round((total - remaining) / 60)
    if (phase === 'focus' && done >= 1) onLog({ subjectId, mins: done })
    setRunning(false)
    setPhase('focus')
    setRemaining(focusMins * 60)
  }

  const R = 86
  const circumference = 2 * Math.PI * R

  return (
    <div className="stack stack--lg">
      <Card className="timer" style={hueVars(hue)}>
        <div className="timer__dial">
          <svg width="190" height="190" viewBox="0 0 190 190">
            <circle cx="95" cy="95" r={R} fill="none" stroke="var(--line)" strokeWidth="8" />
            <circle
              cx="95"
              cy="95"
              r={R}
              fill="none"
              stroke="var(--tint)"
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={circumference * (1 - pct / 100)}
              style={{ transition: 'stroke-dashoffset 1s linear' }}
            />
          </svg>
          <div style={{ display: 'grid', placeItems: 'center' }}>
            <div className="timer__readout">{fmtClock(remaining)}</div>
            <div className="timer__phase">
              {t(phase === 'focus' ? 'study.phaseFocus' : 'study.phaseBreak')}
            </div>
          </div>
        </div>

        {phase === 'focus' && state.subjects.length > 0 && (
          <div className="row row--wrap" style={{ justifyContent: 'center', gap: 'var(--s-2)' }}>
            {state.subjects.map((s) => (
              <button
                key={s.id}
                className="chip"
                aria-pressed={subjectId === s.id}
                onClick={() => setSubjectId(s.id)}
              >
                <span className="chip__dot" style={{ background: `var(--h-${s.hue})` }} />
                {s.name}
              </button>
            ))}
          </div>
        )}

        <div className="row">
          <Button variant="accent" onClick={() => setRunning((r) => !r)} style={{ minWidth: 116 }}>
            {t(running ? 'study.pause' : remaining === total ? 'study.start' : 'study.resume')}
          </Button>
          <Button variant="ghost" onClick={reset} disabled={remaining === total}>
            {t('study.reset')}
          </Button>
          {phase === 'focus' && remaining < total && (
            <Button variant="quiet" onClick={stopAndLog}>
              {t('study.logAndStop')}
            </Button>
          )}
        </div>
      </Card>

      <Card>
        <div className="card__head">
          <h3>{t('study.thisWeek')}</h3>
          <span className="meta">
            {t('study.weekSummary', {
              done: fmtMins(week.totalMins),
              target: fmtMins(week.targetMins),
            })}
          </span>
        </div>

        <div className="bars">
          {week.byDay.map((d) => {
            const max = Math.max(60, ...week.byDay.map((x) => x.mins))
            return (
              <div key={d.date} className="bars__col">
                <div
                  className="bars__bar"
                  style={{
                    height: `${(d.mins / max) * 100}%`,
                    background: d.date === today() ? 'var(--accent)' : 'var(--line-strong)',
                  }}
                  title={`${fmtMins(d.mins)}`}
                />
                <span className="bars__label">{dow[week.byDay.indexOf(d)]}</span>
              </div>
            )
          })}
        </div>

        <div className="stack stack--sm" style={{ marginTop: 'var(--s-5)' }}>
          {week.bySubject.map(({ subject, mins }) => (
            <div key={subject.id} className="row" style={hueVars(subject.hue)}>
              <span className="chip__dot" style={{ background: 'var(--tint)' }} />
              <span className="grow" style={{ fontSize: 'var(--t-sm)' }}>
                {subject.name}
              </span>
              <span className="countdown">
                {fmtMins(mins)} / {fmtMins(subject.targetMins)}
              </span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}

/* ================================ tasks ================================== */

function TasksTab({
  tasks,
  onAdd,
  onToggle,
  onRemove,
}: {
  tasks: Task[]
  onAdd: ReturnType<typeof useStore>['addTask']
  onToggle: (id: string) => void
  onRemove: (id: string) => void
}) {
  const { state } = useStore()
  const t = useT()
  const [draft, setDraft] = useState('')
  const [sheet, setSheet] = useState(false)
  const [subjectId, setSubjectId] = useState<string | undefined>()
  const [due, setDue] = useState('')
  const [priority, setPriority] = useState<0 | 1>(0)

  const open = openTasks(state)
  const done = tasks.filter((t) => t.done).slice(0, 12)

  const quickAdd = () => {
    if (!draft.trim()) return
    onAdd({ title: draft.trim() })
    setDraft('')
  }

  return (
    <div className="stack stack--lg">
      <div className="row">
        <input
          className="input grow"
          placeholder={t('study.addTaskPlaceholder')}
          value={draft}
          dir="auto"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && quickAdd()}
          aria-label={t('study.newTaskLabel')}
        />
        <Button icon variant="accent" onClick={() => setSheet(true)} aria-label={t('study.addWithDetails')}>
          <IconPlus />
        </Button>
      </div>

      <div className="stack stack--sm">
        <Meta>{t('study.openCount', { n: open.length })}</Meta>
        {open.length === 0 ? (
          <Empty title={t('study.nothingOpen')} body={t('study.nothingOpenBody')} />
        ) : (
          open.map((task) => {
            const subject = state.subjects.find((s) => s.id === task.subjectId)
            const overdue = !!task.due && isOverdue(task.due)
            return (
              <Check
                key={task.id}
                checked={task.done}
                hue={subject?.hue}
                onChange={() => onToggle(task.id)}
                label={
                  <>
                    {task.priority === 1 && (
                      <span
                        style={{ color: 'var(--accent)', marginInlineEnd: 4 }}
                        aria-label={t('study.important')}
                      >
                        ★
                      </span>
                    )}
                    {task.title}
                    <span
                      className="countdown"
                      style={{
                        display: 'block',
                        marginTop: 2,
                        color: overdue ? 'var(--danger)' : 'var(--ink-3)',
                      }}
                    >
                      {[subject?.name, task.due && relDay(task.due)].filter(Boolean).join(' · ')}
                    </span>
                  </>
                }
                right={
                  <Button
                    icon
                    variant="quiet"
                    onClick={() => onRemove(task.id)}
                    aria-label={t('study.deleteNamed', { name: task.title })}
                  >
                    <IconTrash />
                  </Button>
                }
              />
            )
          })
        )}
      </div>

      {done.length > 0 && (
        <div className="stack stack--sm">
          <Meta>{t('study.doneSection')}</Meta>
          {done.map((task) => (
            <Check key={task.id} checked onChange={() => onToggle(task.id)} label={task.title} />
          ))}
        </div>
      )}

      {sheet && (
        <Sheet
          title={t('study.newTask')}
          onClose={() => setSheet(false)}
          footer={
            <Button
              variant="accent"
              block
              disabled={!draft.trim()}
              onClick={() => {
                onAdd({
                  title: draft.trim(),
                  subjectId,
                  due: due || undefined,
                  priority,
                })
                setDraft('')
                setDue('')
                setPriority(0)
                setSheet(false)
              }}
            >
              {t('study.addTask')}
            </Button>
          }
        >
          <Field label={t('study.fieldTask')}>
            <input
              className="input"
              dir="auto"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t('study.taskPlaceholder')}
            />
          </Field>

          <div className="field">
            <span>{t('study.fieldSubject')}</span>
            <div className="row row--wrap" style={{ gap: 'var(--s-2)' }}>
              <button className="chip" aria-pressed={!subjectId} onClick={() => setSubjectId(undefined)}>
                {t('common.none')}
              </button>
              {state.subjects.map((s) => (
                <button
                  key={s.id}
                  className="chip"
                  aria-pressed={subjectId === s.id}
                  onClick={() => setSubjectId(s.id)}
                >
                  <span className="chip__dot" style={{ background: `var(--h-${s.hue})` }} />
                  {s.name}
                </button>
              ))}
            </div>
          </div>

          <Field label={t('study.fieldDue')}>
            <input className="input" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          </Field>

          <button
            className="chip"
            style={{ alignSelf: 'flex-start' }}
            aria-pressed={priority === 1}
            onClick={() => setPriority(priority === 1 ? 0 : 1)}
          >
            ★ {t('study.important')}
          </button>
        </Sheet>
      )}
    </div>
  )
}

/* =============================== subjects ================================ */

function SubjectsTab() {
  const { state, addSubject, updateSubject, removeSubject } = useStore()
  const t = useT()
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [hue, setHue] = useState<Hue>('iris')
  const [hours, setHours] = useState(3)

  return (
    <div className="stack">
      {state.subjects.map((s) => (
        <Card key={s.id} style={hueVars(s.hue)}>
          <div className="row">
            <span className="tint__rule" style={{ height: 28 }} />
            <div className="grow">
              <div style={{ fontWeight: 600 }}>{s.name}</div>
              <div className="setting__hint">
                {t('study.targetPerWeek', { x: fmtMins(s.targetMins) })}
              </div>
            </div>
            <Button
              icon
              variant="quiet"
              onClick={() => removeSubject(s.id)}
              aria-label={t('study.deleteNamed', { name: s.name })}
            >
              <IconTrash />
            </Button>
          </div>
          <div className="row" style={{ marginTop: 'var(--s-3)' }}>
            <input
              type="range"
              min={0}
              max={20}
              step={0.5}
              value={s.targetMins / 60}
              onChange={(e) => updateSubject(s.id, { targetMins: Number(e.target.value) * 60 })}
              aria-label={t('study.targetHours', { name: s.name })}
              style={{ flex: 1, accentColor: `var(--h-${s.hue})` }}
            />
            <span className="countdown" style={{ width: 52, textAlign: 'end' }}>
              {fmtMins(s.targetMins)}
            </span>
          </div>
          <div className="row row--wrap" style={{ marginTop: 'var(--s-3)', gap: 'var(--s-2)' }}>
            {HUES.map((h) => (
              <button
                key={h}
                className="swatch"
                aria-label={t('study.setColour', { name: s.name, hue: h })}
                aria-pressed={s.hue === h}
                style={{ background: `var(--h-${h})` }}
                onClick={() => updateSubject(s.id, { hue: h })}
              />
            ))}
          </div>
        </Card>
      ))}

      {adding ? (
        <Card className="stack">
          <Field label={t('study.subjectName')}>
            <input
              className="input"
              value={name}
              dir="auto"
              onChange={(e) => setName(e.target.value)}
              placeholder={t('study.subjectPlaceholder')}
            />
          </Field>
          <div className="row">
            <span className="grow" style={{ fontSize: 'var(--t-sm)' }}>
              {t('study.weeklyTarget')}
            </span>
            <input
              type="range"
              min={0}
              max={20}
              step={0.5}
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
              aria-label={t('study.weeklyTarget')}
            />
            <span className="countdown" style={{ width: 44, textAlign: 'end' }}>
              {fmtMins(hours * 60)}
            </span>
          </div>
          <div className="row row--wrap" style={{ gap: 'var(--s-2)' }}>
            {HUES.map((h) => (
              <button
                key={h}
                className="swatch"
                aria-label={h}
                aria-pressed={hue === h}
                style={{ background: `var(--h-${h})` }}
                onClick={() => setHue(h)}
              />
            ))}
          </div>
          <div className="row">
            <Button
              variant="accent"
              disabled={!name.trim()}
              onClick={() => {
                addSubject({ name: name.trim(), hue, targetMins: hours * 60 })
                setName('')
                setAdding(false)
              }}
            >
              {t('study.addSubject')}
            </Button>
            <Button variant="quiet" onClick={() => setAdding(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        </Card>
      ) : (
        <Button variant="ghost" block onClick={() => setAdding(true)}>
          <IconPlus /> {t('study.addSubject')}
        </Button>
      )}
    </div>
  )
}
